import { useEffect, useRef, useState } from 'react';
import { IHiddenColumnIssues } from '@molecules/formbuilder/controls/Table/controls/tableHiddenIssues';

/** Считать проверку скрытых столбцов на каждое нажатие клавиши нельзя: правила
 *  `required` — формулы, а строк бывают тысячи. Ждём паузу и свободный кадр. */
const IDLE_DELAY_MS = 400;

type IdleHandle = { kind: 'idle'; id: number } | { kind: 'timeout'; id: ReturnType<typeof setTimeout> };

const scheduleIdle = (run: () => void, timeout: number): IdleHandle => {
    if (typeof requestIdleCallback === 'function') {
        return { kind: 'idle', id: requestIdleCallback(run, { timeout }) };
    }
    return { kind: 'timeout', id: setTimeout(run, 0) };
};

const cancelIdle = (handle: IdleHandle) => {
    if (handle.kind === 'idle') cancelIdleCallback(handle.id);
    else clearTimeout(handle.id);
};

/**
 * Ошибки в столбцах, спрятанных набором колонок.
 *
 * Считает владелец правил (EditTable отдаёт `collect`), здесь — только момент
 * пересчёта: пауза после правок плюс свободный кадр. Неудачная проверка формы
 * (`failedEpoch`) пересчитывает сразу — сообщение должно появиться вместе с
 * отказом сохранения, а не спустя полсекунды.
 */
export const useHiddenIssues = (
    collect: ((hiddenFields: string[]) => Promise<IHiddenColumnIssues | null>) | undefined,
    hiddenFields: string[],
    rows: unknown[],
    failedEpoch: number,
): IHiddenColumnIssues | null => {
    const [issues, setIssues] = useState<IHiddenColumnIssues | null>(null);
    const collectRef = useRef(collect);
    collectRef.current = collect;
    // Разделитель заведомо не встречается в имени поля: ключи колонок таблиц форм
    // сами начинаются с '|' («|Name»), и join('|') склеивал их в мусор.
    // Разделитель здесь — \u0001 (как в ключе наблюдений правил колонок).
    const hiddenKey = hiddenFields.join('');
    const fieldsRef = useRef(hiddenFields);
    fieldsRef.current = hiddenFields;
    const lastEpoch = useRef(failedEpoch);

    useEffect(() => {
        if (!collectRef.current || hiddenKey === '') {
            setIssues(null);
            return;
        }
        const fields = fieldsRef.current;
        let alive = true;
        const run = () => {
            collectRef
                .current?.(fields)
                // результат устаревшего прохода не должен перебивать свежий
                .then((result) => alive && setIssues(result))
                // сбой пересчёта не должен ронять таблицу: молчим, как и checkValid
                .catch((error) => console.error('Проверка скрытых столбцов:', error));
        };

        // отказ сохранения — считаем немедленно, остальное терпит паузу
        if (lastEpoch.current !== failedEpoch) {
            lastEpoch.current = failedEpoch;
            run();
            return () => {
                alive = false;
            };
        }
        let idle: IdleHandle | undefined;
        const timer = setTimeout(() => {
            idle = scheduleIdle(run, 2000);
        }, IDLE_DELAY_MS);
        return () => {
            alive = false;
            clearTimeout(timer);
            if (idle) cancelIdle(idle);
        };
    }, [hiddenKey, rows, failedEpoch]);

    return issues;
};
